'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '@/components/Logo';
import { supabase } from '@/lib/supabase';
import { AccessibilityToggle } from './accessibility-toggle';

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

interface LandingNavbarProps {
  mode?: 'landing' | 'standalone';
  topOffset?: number;
  onOpenDesignPartners?: () => void;
}

export function LandingNavbar({ mode = 'landing', topOffset = 0, onOpenDesignPartners }: LandingNavbarProps) {
  const [scrolledState, setScrolledState] = useState(false);
  const isScrolled = mode === 'standalone' ? true : scrolledState;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
  }, []);

  useEffect(() => {
    if (mode === 'standalone') return;
    function handleScroll() {
      setScrolledState(window.scrollY > 50);
    }
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mode]);

  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  // Standalone: dark text, transparent background (no frosted glass on cream)
  // Landing scrolled: dark text, frosted glass white
  // Landing top: light text, transparent
  const useDarkText = mode === 'standalone' || isScrolled;
  const textColor = useDarkText ? '#1C1917' : '#F5F5F4';
  const linkColor = useDarkText ? '#57534E' : 'rgba(255,255,255,0.7)';

  const showFrostedGlass = mode !== 'standalone' && isScrolled;

  return (
    <nav
      style={{
        position: 'fixed',
        top: topOffset,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '16px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.4s',
        background: showFrostedGlass ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: showFrostedGlass ? 'blur(24px) saturate(180%)' : 'none',
        borderBottom: showFrostedGlass ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
      }}
    >
      {/* Left: Logo */}
      <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
        <Logo size={36} variant={useDarkText ? 'light' : 'dark'} />
        <span
          style={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 600,
            fontSize: '1.125rem',
            color: textColor,
            transition: 'color 0.3s',
          }}
        >
          Trustbox{' '}
          <span style={{
            background: 'linear-gradient(135deg, #4ADE80, #22C55E 50%, #06B6D4)',
            backgroundSize: '300% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'nav-gradient-text 5s ease-in-out infinite',
          }}>
            AI
          </span>
        </span>
      </Link>

      {/* Right: Links + CTA (desktop) */}
      <div style={{ display: 'flex', gap: 28, alignItems: 'center' }} className="nav-desktop">
        {mode === 'standalone' ? (
          <>
            <Link
              href="/#how-it-works"
              style={{
                color: linkColor,
                fontSize: '0.9375rem',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'color 0.3s',
              }}
            >
              How It Works
            </Link>
            <Link
              href="/#pricing"
              style={{
                color: linkColor,
                fontSize: '0.9375rem',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'color 0.3s',
              }}
            >
              Pricing
            </Link>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => scrollToSection('how-it-works')}
              style={{
                background: 'none',
                border: 'none',
                color: linkColor,
                fontSize: '0.9375rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.3s',
                padding: 0,
              }}
            >
              How It Works
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('pricing')}
              style={{
                background: 'none',
                border: 'none',
                color: linkColor,
                fontSize: '0.9375rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'color 0.3s',
                padding: 0,
              }}
            >
              Pricing
            </button>
          </>
        )}
        <Link
          href="/learn"
          style={{
            color: linkColor,
            fontSize: '0.9375rem',
            fontWeight: 500,
            textDecoration: 'none',
            transition: 'color 0.3s',
          }}
        >
          Learn
        </Link>
        <Link
          href="/about"
          style={{
            color: linkColor,
            fontSize: '0.9375rem',
            fontWeight: 500,
            textDecoration: 'none',
            transition: 'color 0.3s',
          }}
        >
          About
        </Link>
        {onOpenDesignPartners && (
          <button
            type="button"
            onClick={onOpenDesignPartners}
            style={{
              background: 'none',
              border: 'none',
              color: linkColor,
              fontSize: '0.9375rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'color 0.3s',
              padding: 0,
            }}
          >
            Design Partners
          </button>
        )}
        <AccessibilityToggle isDark={!useDarkText} />
        <Link
          href={isLoggedIn ? '/dashboard' : '/dashboard/simulate'}
          style={{
            padding: '10px 22px',
            background: '#004322',
            color: 'white',
            borderRadius: 999,
            fontSize: '0.875rem',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#166534';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#004322';
            e.currentTarget.style.transform = '';
          }}
        >
          {isLoggedIn ? 'My Dashboard' : 'Start Simulation'}
        </Link>
      </div>

      {/* Mobile hamburger */}
      <div
        className="nav-mobile-btn-container"
        style={{
          display: 'none',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <AccessibilityToggle isDark={!useDarkText} />
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setIsMobileOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            color: textColor,
            cursor: 'pointer',
            padding: 8,
          }}
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200,
              background: '#0A1F12',
              display: 'flex',
              flexDirection: 'column',
            }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 40px' }}>
              <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={closeMobile}>
                <Logo size={36} variant="dark" />
                <span style={{ fontFamily: "'Clash Display', sans-serif", fontWeight: 600, fontSize: '1.125rem', color: '#F5F5F4' }}>
                  Trustbox{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #4ADE80, #22C55E 50%, #06B6D4)',
                    backgroundSize: '300% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'nav-gradient-text 5s ease-in-out infinite',
                  }}>
                    AI
                  </span>
                </span>
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AccessibilityToggle isDark={true} />
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={closeMobile}
                  style={{ background: 'none', border: 'none', color: '#F5F5F4', cursor: 'pointer', padding: 8 }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '32px 40px', flex: 1 }}>
              {mode === 'standalone' ? (
                <>
                  <Link
                    href="/#how-it-works"
                    onClick={closeMobile}
                    style={{ color: '#F5F5F4', fontSize: '1.375rem', fontWeight: 500, textDecoration: 'none', padding: '12px 0' }}
                  >
                    How It Works
                  </Link>
                  <Link
                    href="/#pricing"
                    onClick={closeMobile}
                    style={{ color: '#F5F5F4', fontSize: '1.375rem', fontWeight: 500, textDecoration: 'none', padding: '12px 0' }}
                  >
                    Pricing
                  </Link>
                </>
              ) : (
                [{
                  label: 'How It Works',
                  action: () => { closeMobile(); setTimeout(() => scrollToSection('how-it-works'), 350); },
                }, {
                  label: 'Pricing',
                  action: () => { closeMobile(); setTimeout(() => scrollToSection('pricing'), 350); },
                }].map(({ label, action }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={action}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#F5F5F4',
                      fontSize: '1.375rem',
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                      padding: '12px 0',
                    }}
                  >
                    {label}
                  </button>
                ))
              )}
              <Link
                href="/learn"
                onClick={closeMobile}
                style={{ color: '#F5F5F4', fontSize: '1.375rem', fontWeight: 500, textDecoration: 'none', padding: '12px 0' }}
              >
                Learn
              </Link>
              <Link
                href="/about"
                onClick={closeMobile}
                style={{ color: '#F5F5F4', fontSize: '1.375rem', fontWeight: 500, textDecoration: 'none', padding: '12px 0' }}
              >
                About
              </Link>
              {onOpenDesignPartners && (
                <button
                  type="button"
                  onClick={() => {
                    closeMobile();
                    onOpenDesignPartners();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#F5F5F4',
                    fontSize: '1.375rem',
                    fontWeight: 500,
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: '12px 0',
                  }}
                >
                  Design Partners
                </button>
              )}

              <div style={{ marginTop: 32 }}>
                <Link
                  href={isLoggedIn ? '/dashboard' : '/dashboard/simulate'}
                  onClick={closeMobile}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '14px 32px',
                    background: '#004322',
                    color: 'white',
                    borderRadius: 999,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {isLoggedIn ? 'My Dashboard' : 'Start Simulation'}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        @keyframes nav-gradient-text {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }
        @media (max-width: 768px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn-container { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}
